import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { listenToAdvisoryWorkspaceFact } from '../../services/advisoryService';
import {
  newerWorkspace,
  parseWorkspaceSnapshot,
  type WorkspaceAction,
  type WorkspaceRequest,
  type WorkspaceResult,
  type WorkspaceSnapshot,
} from './advisoryWorkspace';
import type { SupervisorTurnOutcome, SupervisorTurnRequest } from './useSupervisorTurn';
export type WorkspaceReadStatus = 'loading' | 'ready' | 'absent' | 'denied' | 'failed' | 'invalid';
type State = {
  scope: string;
  snapshot: WorkspaceSnapshot | null;
  readStatus: WorkspaceReadStatus;
  authoritative: boolean;
  pending: WorkspaceRequest | null;
  busy: boolean;
  error: string | null;
  stale: boolean;
};
const empty = (scope: string): State => ({
  scope,
  snapshot: null,
  readStatus: 'loading',
  authoritative: false,
  pending: null,
  busy: false,
  error: null,
  stale: false,
});
const storageKey = (scope: string) => 'advisory-workspace-command:' + scope;
function savedRequest(scope: string): WorkspaceRequest | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey(scope)) ?? 'null');
    return value &&
      typeof value.requestId === 'string' &&
      Number.isSafeInteger(value.expectedSetupRevision) &&
      typeof value.operation === 'string'
      ? value
      : null;
  } catch {
    return null;
  }
}
function clearSavedRequest(scope: string, request: WorkspaceRequest) {
  if (savedRequest(scope)?.requestId === request.requestId)
    sessionStorage.removeItem(storageKey(scope));
}
/** Serializes short commands; Firestore drives progress independently of any browser stream. */
export function useAdvisoryWorkspace({
  userId,
  projectId,
  send,
}: {
  userId?: string;
  projectId?: string;
  send: (request: SupervisorTurnRequest) => Promise<SupervisorTurnOutcome>;
}) {
  const scope = `${userId ?? ''}/${projectId ?? ''}`;
  const [state, setState] = useState<State>(() => ({
    ...empty(scope),
    pending: savedRequest(scope),
  }));
  const current = useRef(state),
    identity = useRef(scope),
    mounted = useRef(false),
    inFlight = useRef<string | null>(null);
  if (identity.current !== scope) {
    identity.current = scope;
    current.current = { ...empty(scope), pending: savedRequest(scope) };
    inFlight.current = null;
  }
  const [readAttempt, setReadAttempt] = useState(0);
  const update = useCallback((change: (old: State) => State) => {
    if (!mounted.current) return;
    current.current = change(current.current);
    setState(current.current);
  }, []);
  const receive = useCallback(
    (next: WorkspaceSnapshot, fromRead = false, fromCache = false) =>
      update(old => {
        const snapshot = newerWorkspace(old.snapshot, next);
        const stale =
          old.stale ||
          Boolean(
            old.snapshot &&
              snapshot.setupRevision > old.snapshot.setupRevision &&
              !inFlight.current &&
              !snapshot.locked
          );
        return {
          ...old,
          snapshot,
          stale,
          readStatus: fromRead ? 'ready' : old.readStatus,
          authoritative: fromRead ? !fromCache : old.authoritative,
        };
      }),
    [update]
  );
  useEffect(() => {
    mounted.current = true;
    update(old => ({ ...old, readStatus: 'loading', authoritative: false }));
    if (!userId || !projectId) return;
    let live = true;
    const stop = listenToAdvisoryWorkspaceFact(
      userId,
      projectId,
      (raw, cache = false) => {
        if (!live || identity.current !== scope) return;
        if (!raw) {
          update(old => ({ ...old, readStatus: 'absent', authoritative: false }));
          return;
        }
        const snapshot = parseWorkspaceSnapshot(raw.data, projectId);
        if (snapshot) receive(snapshot, true, cache);
        else update(old => ({ ...old, readStatus: 'invalid', authoritative: false }));
      },
      code => {
        if (live && identity.current === scope)
          update(old => ({
            ...old,
            readStatus: code.includes('permission-denied') ? 'denied' : 'failed',
            authoritative: false,
          }));
      }
    );
    return () => {
      live = false;
      mounted.current = false;
      stop();
    };
  }, [scope, userId, projectId, readAttempt, receive, update]);
  const execute = useCallback(
    async (
      action: WorkspaceAction | WorkspaceRequest,
      retry = false
    ): Promise<WorkspaceResult | null> => {
      const before = current.current;
      if (
        !projectId ||
        inFlight.current ||
        before.scope !== scope ||
        (!retry &&
          (before.pending ||
            before.stale ||
            !before.authoritative ||
            before.readStatus !== 'ready' ||
            !before.snapshot))
      )
        return null;
      const request: WorkspaceRequest = retry
        ? (action as WorkspaceRequest)
        : {
            ...action,
            requestId: uuidv4(),
            expectedSetupRevision: before.snapshot?.setupRevision ?? 1,
          };
      try {
        sessionStorage.setItem(storageKey(scope), JSON.stringify(request));
      } catch {
        update(old => ({ ...old, error: 'advisory:workspace.live.storageFailed' }));
        return null;
      }
      inFlight.current = request.requestId;
      update(old => ({ ...old, busy: true, pending: request, error: null }));
      try {
        const outcome = await send({ projectId, workspace: request });
        if (
          !mounted.current ||
          identity.current !== scope ||
          current.current.pending?.requestId !== request.requestId
        )
          return null;
        if (outcome.workspace) receive(outcome.workspace);
        if (outcome.workspaceResult && !outcome.workspaceResult.matching) {
          clearSavedRequest(scope, request);
          update(old => ({ ...old, pending: null, error: null, stale: false }));
          return outcome.workspaceResult;
        }
        if (outcome.workspaceFailure === 'rejected') {
          clearSavedRequest(scope, request);
          update(old => ({
            ...old,
            pending: null,
            stale: Boolean(
              old.snapshot && old.snapshot.setupRevision !== request.expectedSetupRevision
            ),
            error: 'advisory:workspace.live.rejected',
          }));
        } else
          update(old => ({
            ...old,
            error: outcome.workspaceResult?.matching
              ? 'advisory:workspace.live.matching'
              : 'advisory:workspace.live.uncertain',
          }));
        return null;
      } catch {
        if (identity.current === scope && current.current.pending?.requestId === request.requestId)
          update(old => ({ ...old, error: 'advisory:workspace.live.uncertain' }));
        return null;
      } finally {
        if (identity.current === scope && inFlight.current === request.requestId) {
          inFlight.current = null;
          update(old => ({ ...old, busy: false }));
        }
      }
    },
    [projectId, scope, receive, send, update]
  );
  const visible = state.scope === scope ? state : current.current;
  return {
    ...visible,
    execute,
    reload: () => setReadAttempt(value => value + 1),
    retry: () =>
      current.current.pending ? execute(current.current.pending, true) : Promise.resolve(null),
    acceptLatest: () => update(old => ({ ...old, stale: false, error: null })),
  };
}
export type AdvisoryWorkspaceController = ReturnType<typeof useAdvisoryWorkspace>;
